import React, { useState, useCallback } from 'react';
import { Box, Typography, IconButton } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import type { CommodityGroupTreeNode } from '../../lib/orderPickingItems';

interface Props {
  tree: CommodityGroupTreeNode[];
  renderItems: (cgId: string) => React.ReactNode;
}

/** Mirrors mobile react-native-final-tree-view: node row + optional product list per CG id. */
const CommodityGroupTree = (props: Props) => {
  const { tree, renderItems } = props;
  const [productsExpansion, setProductsExpansion] = useState<Record<string, boolean>>({});

  const isProductsExpanded = useCallback(
    (cgId: string) => (productsExpansion[cgId] === undefined ? true : productsExpansion[cgId]),
    [productsExpansion],
  );

  const expandProducts = useCallback((cgId: string) => {
    setProductsExpansion((prev) => ({
      ...prev,
      [cgId]: !isProductsExpanded(cgId),
    }));
  }, [isProductsExpanded]);

  const renderNode = (node: CommodityGroupTreeNode, level: number) => {
    const hasChildren = node.children && node.children.length > 0;
    const showList = node.hasProducts && isProductsExpanded(node.id);

    return (
      <Box key={node.id} pl={Math.min(level * 2, 24)}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bgcolor="#f0f0f0"
          py={0.5}
          px={0.5}
        >
          <Box display="flex" alignItems="center" flex={1} minWidth={0}>
            <Typography variant="body2" style={{ marginLeft: 4, wordBreak: 'break-word' }}>
              {hasChildren ? '▸' : '•'} {node.name}
            </Typography>
          </Box>
          {node.hasProducts && (
            <IconButton size="small" aria-label="toggle products" onClick={() => expandProducts(node.id)}>
              {showList ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
        {showList && <Box pl={1}>{renderItems(node.id)}</Box>}
        {hasChildren &&
          node.children.map((ch) => renderNode(ch, level + 1))}
      </Box>
    );
  };

  return (
    <Box>
      {tree.map((n) => (
        <Box key={n.id} mb={0.5}>
          {renderNode(n, 0)}
        </Box>
      ))}
    </Box>
  );
};

export default CommodityGroupTree;
